import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - Tasks & Follow-ups API
// =============================================================================

export const royalMomentumTasksAndFollowupsApi = createApi({
    reducerPath: "royalMomentumTasksAndFollowupsApi",
    baseQuery: apiRequestBaseQuery,
    // "Meetings" is invalidated from royalMomentumMeetingsApi's addMeeting via
    // `util.invalidateTags` — tags do not cross a `createApi` boundary on their own.
    tagTypes: ["TaskStatistics", "Tasks", "Meetings"],
    endpoints: (builder) => ({
        // Task Statistics (summary cards) — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getTaskStatistics: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTaskStatisticsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                    },
                };
            },
            providesTags: ["TaskStatistics"],
        }),

        // Create Task — V2 (`agency_id` replaces `travel_agency_uid`).
        createTask: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.addTaskV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        title: params.title,
                        meeting_uid: params.meetingUid || null,
                        due_date: params.dueDate,
                        priority: params.priority,
                        assign_to: params.assignTo,
                        description: params.description || null,
                        status: params.status || "open",
                    },
                };
            },
            invalidatesTags: ["TaskStatistics", "Tasks"],
        }),

        // Get Meetings by Agency (for Related Meeting dropdown) — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getListMeetingsByAgency: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getMeetingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        show: "upcoming",
                    },
                };
            },
            providesTags: ["Meetings"],
            keepUnusedDataFor: 0,
        }),

        // Get Contacts (for Assign To dropdown) — V2.
        // `agency_id` replaces `travel_agency_uid`; contact_type "internal" = agency-internal
        // contacts (per GET_CONTACTS_V2 spec). Shared endpoint with royalMomentumContactsApi.
        getContacts: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getContactsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        contact_type: "internal",
                    },
                };
            },
        }),

        // Get Tasks — V2 (`agency_id` replaces `travel_agency_uid`).
        getTasks: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTasksV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        status: params.status || null,
                        priority: params.priority || null,
                        assign_to: params.assignTo || null,
                    },
                };
            },
            providesTags: ["Tasks"],
        }),

        // Update Task — V2 (`agency_id` replaces `travel_agency_uid`).
        updateTask: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.updateTaskV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        task_uid: params.taskUid,
                        title: params.title,
                        meeting_uid: params.meetingUid || null,
                        due_date: params.dueDate,
                        priority: params.priority,
                        assign_to: params.assignTo,
                        description: params.description || null,
                        status: params.status,
                    },
                };
            },
            invalidatesTags: ["TaskStatistics", "Tasks"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    useGetTaskStatisticsQuery,
    useGetListMeetingsByAgencyQuery,
    useGetContactsQuery,
    useGetTasksQuery,
    useCreateTaskMutation,
    useUpdateTaskMutation,
} = royalMomentumTasksAndFollowupsApi;
